/**
 * Per-status dot palette, in the two forms the UI needs:
 *
 * - `pseudo` paints a `::before` dot (used by `Badge` in the loads table).
 * - `plain` paints a real element (used by the active pill in the load-details status strip).
 *
 * Both forms are spelled out per entry rather than derived from one another, because Tailwind only
 * generates classes it can find as literal source text — a runtime `replaceAll('before:', '')`
 * produces class names the scanner never sees, and the dot renders transparent. Keeping the pair on
 * one row is what makes a drifting hex edit visible.
 */
const LOAD_STATUS_DOTS: Record<string, { plain: string; pseudo: string }> = {
  arrived_at_collection: {
    plain: 'border bg-[#5EEAD4] border-[#047857] dark:bg-[#4D7C0F] dark:border-[#4D7C0F]',
    pseudo:
      'before:border before:bg-[#5EEAD4] before:border-[#047857] dark:before:bg-[#4D7C0F] dark:before:border-[#4D7C0F]',
  },
  arrived_at_destination: {
    plain: 'border bg-[#4D7C0F] border-[#4D7C0F] dark:bg-[#2DD4BF] dark:border-[#047857]',
    pseudo:
      'before:border before:bg-[#4D7C0F] before:border-[#4D7C0F] dark:before:bg-[#2DD4BF] dark:before:border-[#047857]',
  },
  check_rate: {
    plain: 'border bg-[#3B82F6] border-[#3B82F6] dark:bg-[#0369A1] dark:border-[#0369A1]',
    pseudo:
      'before:border before:bg-[#3B82F6] before:border-[#3B82F6] dark:before:bg-[#0369A1] dark:before:border-[#0369A1]',
  },
  completed: {
    plain: 'border bg-[#0A0A0A] border-[#0A0A0A] dark:bg-white dark:border-white',
    pseudo:
      'before:border before:bg-[#0A0A0A] before:border-[#0A0A0A] dark:before:bg-white dark:before:border-white',
  },
  converted_to_load: {
    plain: 'border bg-[#1E3A8A] border-[#1E3A8A] dark:bg-[#60A5FA] dark:border-[#60A5FA]',
    pseudo:
      'before:border before:bg-[#1E3A8A] before:border-[#1E3A8A] dark:before:bg-[#60A5FA] dark:before:border-[#60A5FA]',
  },
  in_transit: {
    plain: 'border bg-[#16A34A] border-[#16A34A] dark:bg-[#4ADE80] dark:border-[#15803D]',
    pseudo:
      'before:border before:bg-[#16A34A] before:border-[#16A34A] dark:before:bg-[#4ADE80] dark:before:border-[#15803D]',
  },
  loading_started: {
    plain: 'border bg-[#4ADE80] border-[#15803D] dark:bg-[#16A34A] dark:border-[#16A34A]',
    pseudo:
      'before:border before:bg-[#4ADE80] before:border-[#15803D] dark:before:bg-[#16A34A] dark:before:border-[#16A34A]',
  },
  need_details: {
    plain: 'border bg-[#60A5FA] border-[#60A5FA] dark:bg-[#1E3A8A] dark:border-[#1E3A8A]',
    pseudo:
      'before:border before:bg-[#60A5FA] before:border-[#60A5FA] dark:before:bg-[#1E3A8A] dark:before:border-[#1E3A8A]',
  },
  new_inquiry: {
    plain: 'border bg-[#DBEAFE] border-[#60A5FA] dark:bg-[#DBEAFE] dark:border-[#60A5FA]',
    pseudo:
      'before:border before:bg-[#DBEAFE] before:border-[#60A5FA] dark:before:bg-[#DBEAFE] dark:before:border-[#60A5FA]',
  },
  payment_validation: {
    plain: 'border bg-[#0369A1] border-[#0369A1] dark:bg-[#3B82F6] dark:border-[#3B82F6]',
    pseudo:
      'before:border before:bg-[#0369A1] before:border-[#0369A1] dark:before:bg-[#3B82F6] dark:before:border-[#3B82F6]',
  },
  quoted: {
    plain: 'border bg-[#1D4ED8] border-[#1D4ED8] dark:bg-[#1D4ED8] dark:border-[#1D4ED8]',
    pseudo:
      'before:border before:bg-[#1D4ED8] before:border-[#1D4ED8] dark:before:bg-[#1D4ED8] dark:before:border-[#1D4ED8]',
  },
  to_cover: {
    plain: 'border bg-[#FEF3C7] border-[#FBBF24] dark:bg-[#D97706] dark:border-[#D97706]',
    pseudo:
      'before:border before:bg-[#FEF3C7] before:border-[#FBBF24] dark:before:bg-[#D97706] dark:before:border-[#D97706]',
  },
  transport_planning: {
    plain: 'border bg-[#FACC15] border-[#FACC15] dark:bg-[#FACC15] dark:border-[#FACC15]',
    pseudo:
      'before:border before:bg-[#FACC15] before:border-[#FACC15] dark:before:bg-[#FACC15] dark:before:border-[#FACC15]',
  },
  unloading_confirmed: {
    plain: 'border bg-[#166534] border-[#166534] dark:bg-[#D1FAE5] dark:border-[#10B981]',
    pseudo:
      'before:border before:bg-[#166534] before:border-[#166534] dark:before:bg-[#D1FAE5] dark:before:border-[#10B981]',
  },
  waiting_for_arrival_confirmation: {
    plain: 'border bg-[#D1FAE5] border-[#10B981] dark:bg-[#166534] dark:border-[#166534]',
    pseudo:
      'before:border before:bg-[#D1FAE5] before:border-[#10B981] dark:before:bg-[#166534] dark:before:border-[#166534]',
  },
  waiting_for_docs: {
    plain: 'border bg-[#71717A] border-[#71717A] dark:bg-[#71717A] dark:border-[#71717A]',
    pseudo:
      'before:border before:bg-[#71717A] before:border-[#71717A] dark:before:bg-[#71717A] dark:before:border-[#71717A]',
  },
  waiting_for_transit: {
    plain: 'border bg-[#D97706] border-[#D97706] dark:bg-[#FEF3C7] dark:border-[#FBBF24]',
    pseudo:
      'before:border before:bg-[#D97706] before:border-[#D97706] dark:before:bg-[#FEF3C7] dark:before:border-[#FBBF24]',
  },
}

const LOAD_STATUS_ICON_CLASSES: Record<string, string> = {
  archived: 'stroke-[#0A0A0A] dark:stroke-[#FAFAFA]',
  arrived_at_collection:
    'fill-[#5EEAD4] stroke-[#047857] dark:fill-[#4D7C0F] dark:stroke-[#4D7C0F]',
  arrived_at_destination:
    'fill-[#4D7C0F] stroke-[#4D7C0F] dark:fill-[#2DD4BF] dark:stroke-[#047857]',
  cancelled: 'stroke-[#0A0A0A] dark:stroke-[#FAFAFA]',
  check_rate: 'fill-[#3B82F6] stroke-[#3B82F6] dark:fill-[#0369A1] dark:stroke-[#0369A1]',
  completed: 'fill-[#0A0A0A] stroke-[#0A0A0A] dark:fill-white dark:stroke-white',
  converted_to_load: 'fill-[#1E3A8A] stroke-[#1E3A8A] dark:fill-[#60A5FA] dark:stroke-[#60A5FA]',
  in_transit: 'fill-[#16A34A] stroke-[#16A34A] dark:fill-[#4ADE80] dark:stroke-[#15803D]',
  loading_started: 'fill-[#4ADE80] stroke-[#15803D] dark:fill-[#16A34A] dark:stroke-[#16A34A]',
  need_details: 'fill-[#60A5FA] stroke-[#60A5FA] dark:fill-[#1E3A8A] dark:stroke-[#1E3A8A]',
  new_inquiry: 'fill-[#DBEAFE] stroke-[#60A5FA] dark:fill-[#DBEAFE] dark:stroke-[#60A5FA]',
  payment_validation: 'fill-[#0369A1] stroke-[#0369A1] dark:fill-[#3B82F6] dark:stroke-[#3B82F6]',
  quoted: 'fill-[#1D4ED8] stroke-[#1D4ED8] dark:fill-[#1D4ED8] dark:stroke-[#1D4ED8]',
  to_cover: 'fill-[#FEF3C7] stroke-[#FBBF24] dark:fill-[#D97706] dark:stroke-[#D97706]',
  transport_planning: 'fill-[#FACC15] stroke-[#FACC15] dark:fill-[#FACC15] dark:stroke-[#FACC15]',
  unloading_confirmed: 'fill-[#166534] stroke-[#166534] dark:fill-[#D1FAE5] dark:stroke-[#10B981]',
  waiting_for_arrival_confirmation:
    'fill-[#D1FAE5] stroke-[#10B981] dark:fill-[#166534] dark:stroke-[#166534]',
  waiting_for_docs: 'fill-[#71717A] stroke-[#71717A] dark:fill-[#71717A] dark:stroke-[#71717A]',
  waiting_for_transit: 'fill-[#D97706] stroke-[#D97706] dark:fill-[#FEF3C7] dark:stroke-[#FBBF24]',
}

export const getLoadStatusDotClass = (alias: string) => LOAD_STATUS_DOTS[alias]?.pseudo

export const getLoadStatusDotSpanClass = (alias: string) => LOAD_STATUS_DOTS[alias]?.plain

export const getLoadStatusIconClass = (alias: string) => LOAD_STATUS_ICON_CLASSES[alias]
